/* eslint-disable no-case-declarations */
// components/common/table/BaseTableCell.tsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  TASK_STATUS,
  TASK_PRIORITIES,
  FREQUENCY_OPTIONS,
  COLORS,
} from "@/utils/constants";
import { PriorityBadge } from "@/components/common/PriorityBadge";
import CustomDropdown from "./CustomDropdown";
import ModernDatePicker from "./ModernDatePicker";
import clsx from "clsx";
import { File, MessageCircle, Clock, Trash2 } from "lucide-react";
import { Task, Column } from "@/types";
import Tooltip from "../Tooltip";
import { DropdownConfig } from "./BaseTable";
import EnhancedDatePicker from "./EnhancedDatePicker";
import FrequencyPicker from "./FrequencyPicker";

// Define color configurations for easy customization
const PRIORITY_COLORS = {
  [TASK_PRIORITIES.HIGH]: {
    color: "#DC2626",
    bgColor: "#FEE2E2",
    borderColor: "#DC2626",
  },
  [TASK_PRIORITIES.MEDIUM]: {
    color: "#F59E0B",
    bgColor: "#FEF3C7",
    borderColor: "#F59E0B",
  },
  [TASK_PRIORITIES.LOW]: {
    color: "#10B981",
    bgColor: "#D1FAE5",
    borderColor: "#10B981",
  },
};

const STATUS_COLORS = {
  [TASK_STATUS.TODO]: {
    color: "#EF4444",
    bgColor: "#FEE2E2",
    borderColor: "#EF4444",
  },
  [TASK_STATUS.IN_PROCESS]: {
    color: "#F59E0B",
    bgColor: "#FEF3C7",
    borderColor: "#F59E0B",
  },
  [TASK_STATUS.IN_REVIEW]: {
    color: "#8B5CF6",
    bgColor: "#EDE9FE",
    borderColor: "#8B5CF6",
  },
  [TASK_STATUS.COMPLETED]: {
    color: "#10B981",
    bgColor: "#D1FAE5",
    borderColor: "#10B981",
  },
  [TASK_STATUS.ARCHIVED]: {
    color: "#6B7280",
    bgColor: "#F3F4F6",
    borderColor: "#6B7280",
  },
  [TASK_STATUS.HELP_REQUIRED]: {
    color: "#F97316",
    bgColor: "#FED7AA",
    borderColor: "#F97316",
  },
};

interface BaseTableCellProps {
  task: Task;
  column: Column;
  index: number;
  isNewTask: boolean;
  hasRealId?: boolean;
  isEditing: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  onNewTaskChange?: (tempId: string, key: keyof Task, value: any) => void;
  onExistingTaskChange?: (taskId: string, key: keyof Task, value: any) => void;
  clientsList: { _id: string; name: string }[];
  users: { _id: string; name: string }[];
  openModal: (type: string, task: Task) => void;
  serialStart: number;
  dropdownConfig?: DropdownConfig;
}

// Helper function to get status display with user name for Help Required
const getStatusDisplay = (
  task: Task,
  users: { _id: string; name: string }[]
) => {
  if (task.status === TASK_STATUS.HELP_REQUIRED && task.helpRequiredUser) {
    const user = users.find((u) => u._id === task.helpRequiredUser);
    return user ? `Help Required - ${user.name}` : "Help Required";
  }

  // Return normal status labels
  const statusLabels: Record<string, string> = {
    [TASK_STATUS.TODO]: "To Do",
    [TASK_STATUS.IN_PROCESS]: "In Process",
    [TASK_STATUS.IN_REVIEW]: "In Review",
    [TASK_STATUS.COMPLETED]: "Completed",
    [TASK_STATUS.ARCHIVED]: "Archived",
    [TASK_STATUS.HELP_REQUIRED]: "Help Required",
  };

  return statusLabels[task.status as string] || task.status;
};

export const BaseTableCell: React.FC<BaseTableCellProps> = ({
  task,
  column,
  index,
  isNewTask,
  hasRealId = false,
  isEditing,
  onEditStart,
  onEditEnd,
  onNewTaskChange,
  onExistingTaskChange,
  clientsList,
  users,
  openModal,
  serialStart,
  dropdownConfig = {},
}) => {
  const colKey = column.key as string;
  const inputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  // State for Help Required user selection
  const [helpUserValue, setHelpUserValue] = useState(
    task.helpRequiredUser || ""
  );
  const [submenuPosition, setSubmenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [submenuSearchTerm, setSubmenuSearchTerm] = useState("");
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  
  // ✅ ADDED: Date range state
  const [dateRange, setDateRange] = useState({
    startDate: task.startDate || "",
    endDate: task.endDate || ""
  });

  const [localValue, setLocalValue] = useState<any>(() => {
    const currentValue = task[colKey as keyof Task];
    if (!currentValue && currentValue !== 0) {
      switch (colKey) {
        case "priority":
          return TASK_PRIORITIES.MEDIUM;
        case "status":
          return TASK_STATUS.TODO;
        case "frequency":
          return FREQUENCY_OPTIONS.NONE;
        case "et":
        case "tt":
          return 0;
        default:
          return "";
      }
    }
    return currentValue;
  });

  const [lastCommittedValue, setLastCommittedValue] = useState<any>(localValue);

  // Create user options for Help Required at component level
  const userOptionsForHelp = useMemo(() => {
    return users.map((user) => ({
      value: user._id,
      label: user.name,
    }));
  }, [users]);

  // Filter users based on search term - at component level
  const filteredUserOptions = useMemo(() => {
    if (!submenuSearchTerm) return userOptionsForHelp;
    return userOptionsForHelp.filter((user) =>
      user.label.toLowerCase().includes(submenuSearchTerm.toLowerCase())
    );
  }, [userOptionsForHelp, submenuSearchTerm]);

  useEffect(() => {
    const newValue = task[colKey as keyof Task];
    setLocalValue(newValue);
    setLastCommittedValue(newValue);

    // Update help user value when task changes
    if (colKey === "status") {
      setHelpUserValue(task.helpRequiredUser || "");
    }
  }, [task, colKey]);

  // ✅ ADDED: Update date range when task changes
  useEffect(() => {
    setDateRange({
      startDate: task.startDate || "",
      endDate: task.endDate || ""
    });
  }, [task.startDate, task.endDate]);

  useEffect(() => {
    if (isEditing) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          try {
            inputRef.current.select();
          } catch {}
        }
      }, 0);
    }
  }, [isEditing]);

  useEffect(() => {
    if (isEditing && colKey === "dueDate" && dateInputRef.current) {
      dateInputRef.current.focus();
      setTimeout(() => {
        dateInputRef.current?.showPicker?.();
      }, 0);
      setTimeout(() => {
        dateInputRef.current?.click();
      }, 50);
    }
  }, [isEditing, colKey]);

  // Close submenu when clicking outside
  useEffect(() => {
    if (!submenuPosition) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (submenuRef.current && !submenuRef.current.contains(target)) {
        const mainDropdown = document.querySelector('[role="listbox"]');
        if (mainDropdown && !mainDropdown.contains(target)) {
          setSubmenuPosition(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [submenuPosition]);

  const getDisplayValue = (value: any, columnKey: string): string => {
    if (value === null || value === undefined || value === "") {
      const placeholders: Record<string, string> = {
        client: "Select Client",
        task: "Enter Task",
        subTask: "Enter Sub-Task",
        et: "0",
        allottedTo: "Select User",
        teamLeader: "Select User",
        priority: "Medium",
        dueDate: "Select Date",
        frequency: "None",
        status: "To Do",
        tt: "0",
      };
      return placeholders[columnKey] || "-";
    }

    if (columnKey === "client" && value && typeof value === "string") {
      const client = clientsList.find((c) => c._id === value);
      return client ? client.name : value;
    }

    if (
      (columnKey === "allottedTo" || columnKey === "teamLeader") &&
      value &&
      typeof value === "string"
    ) {
      const user = users.find((u) => u._id === value);
      return user ? user.name : value;
    }

    if (typeof value === "object" && value !== null) {
      return value.name || value.label || "-";
    }

    return String(value);
  };

  const handleCellClick = (e: React.MouseEvent) => {
    if (colKey === "serial" || colKey === "actions") return;

    const target = e.target as HTMLElement;
    if (
      target.tagName === "SELECT" ||
      target.tagName === "INPUT" ||
      target.tagName === "BUTTON"
    ) {
      return;
    }

    if (!isEditing) {
      e.stopPropagation();
      onEditStart();
    }
  };

  const commitChange = () => {
    let currentValue = localValue;

    if (colKey === "et" || colKey === "tt") {
      currentValue = parseInt(currentValue || "0") || 0;
    }

    // Check if value changed
    if (currentValue === lastCommittedValue) return;

    setLastCommittedValue(currentValue);

    // For new tasks (might have real ID but still in newTaskRows)
    if (isNewTask && task.tempId) {
      if (onNewTaskChange) {
        onNewTaskChange(task.tempId, colKey as keyof Task, currentValue);
      }
    }
    // For existing tasks (not in newTaskRows)
    else if (!isNewTask && hasRealId && onExistingTaskChange && task._id) {
      onExistingTaskChange(task._id, colKey as keyof Task, currentValue);
    }
  };

  const handleLocalChange = (value: any) => {
    setLocalValue(value);
  };

  const moveToNextCell = (shiftKey: boolean = false) => {
    const currentTd = cellRef.current?.closest("td");
    if (!currentTd) return;

    if (shiftKey) {
      const prevTd = currentTd.previousElementSibling as HTMLElement;
      if (prevTd && prevTd.querySelector("[data-excel-cell]")) {
        const prevCell = prevTd.querySelector(
          "[data-excel-cell]"
                  ) as HTMLElement;
        prevCell?.click();
      } else {
        const prevRow = currentTd.closest("tr")
          ?.previousElementSibling as HTMLElement;
        if (prevRow) {
          const lastCell = prevRow.querySelector(
            "td:last-child [data-excel-cell]"
          ) as HTMLElement;
          lastCell?.click();
        }
      }
    } else {
      const nextTd = currentTd.nextElementSibling as HTMLElement;
      if (nextTd && nextTd.querySelector("[data-excel-cell]")) {
        const nextCell = nextTd.querySelector(
          "[data-excel-cell]"
        ) as HTMLElement;
        nextCell?.click();
      } else {
        const currentRow = currentTd.closest("tr");
        const nextRow = currentRow?.nextElementSibling as HTMLElement;
        if (nextRow) {
          const firstEditableCell = nextRow.querySelector(
            "td [data-excel-cell]"
          ) as HTMLElement;
          firstEditableCell?.click();
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commitChange();
      onEditEnd();
      const currentTd = cellRef.current?.closest("td");
      const currentRow = currentTd?.closest("tr");
      const nextRow = currentRow?.nextElementSibling as HTMLElement;
      if (nextRow && currentTd) {
        const cellIndex = Array.from(
          currentTd.parentElement?.children || []
        ).indexOf(currentTd);
        const nextCell = nextRow.children[cellIndex]?.querySelector(
          "[data-excel-cell]"
        ) as HTMLElement;
        nextCell?.click();
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      commitChange();
      onEditEnd();
      moveToNextCell(e.shiftKey);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setLocalValue(task[colKey as keyof Task]);
      setSubmenuPosition(null);
      onEditEnd();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (
        !cellRef.current?.contains(document.activeElement) &&
        !document
          .getElementById("dropdown-portal")
          ?.contains(document.activeElement) &&
        (!submenuRef.current ||
          !submenuRef.current.contains(document.activeElement))
      ) {
        commitChange();
        onEditEnd();
      }
    }, 200);
  };

  const renderEditInterface = () => {
    const editingValue =
      localValue !== undefined ? localValue : task[colKey as keyof Task];

    // Common style for ALL inputs - exact 24px height
    const commonInputStyle = {
      height: "24px",
      minHeight: "24px",
      width: "100%",
    };

    const commonInputProps = {
      ref: inputRef,
      value: editingValue ?? "",
      onKeyDown: handleKeyDown,
      onBlur: handleBlur,
      className:
        "w-full h-full border border-blue-500 rounded-none px-2 outline-none focus:border-blue-600 text-sm bg-white text-black",
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
      autoFocus: true,
      style: commonInputStyle,
    };

    switch (colKey) {
      case "client":
        const clientOptions = clientsList.map((client) => ({
          value: client._id,
          label: client.name,
        }));

        return (
          <CustomDropdown
            dropdownWidth={220}
            minDropdownWidth={220}
            value={editingValue?._id || editingValue || ""}
            options={clientOptions}
            onChange={(value) => handleLocalChange(value)}
            placeholder="Select Client"
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus={true}
            autoOpen={true}
            showAddButton={true}
            addLabel="Add"
            onAddClick={() => {
              const addHandler = dropdownConfig.client?.onAdd;
              if (addHandler) {
                addHandler();
              }
            }}
          />
        );

      case "allottedTo":
      case "teamLeader":
        const userOptions = users.map((user) => ({
          value: user._id,
          label: user.name,
        }));

        return (
          <CustomDropdown
            dropdownWidth={220}
            minDropdownWidth={220}
            value={editingValue?._id || editingValue || ""}
            options={userOptions}
            onChange={(value) => handleLocalChange(value)}
            placeholder={
              colKey === "allottedTo" ? "Select User" : "Select Team Leader"
            }
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus={true}
            autoOpen={true}
          />
        );

      case "priority":
        const priorityOptions = [
          {
            value: TASK_PRIORITIES.HIGH,
            label: "High",
            ...PRIORITY_COLORS[TASK_PRIORITIES.HIGH],
          },
          {
            value: TASK_PRIORITIES.MEDIUM,
            label: "Medium",
            ...PRIORITY_COLORS[TASK_PRIORITIES.MEDIUM],
          },
          {
            value: TASK_PRIORITIES.LOW,
            label: "Low",
            ...PRIORITY_COLORS[TASK_PRIORITIES.LOW],
          },
        ];

        return (
          <CustomDropdown
            dropdownWidth={220}
            minDropdownWidth={220}
            value={String(editingValue || TASK_PRIORITIES.MEDIUM)}
            options={priorityOptions}
            onChange={(value) => handleLocalChange(value)}
            placeholder="Select Priority"
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus={true}
            autoOpen={true}
          />
        );

      case "status":
        const statusOptions = [
          {
            value: TASK_STATUS.TODO,
            label: "To Do",
            ...STATUS_COLORS[TASK_STATUS.TODO],
          },
          {
            value: TASK_STATUS.IN_PROCESS,
            label: "In Process",
            ...STATUS_COLORS[TASK_STATUS.IN_PROCESS],
          },
          {
            value: TASK_STATUS.IN_REVIEW,
            label: "In Review",
            ...STATUS_COLORS[TASK_STATUS.IN_REVIEW],
          },
          {
            value: TASK_STATUS.COMPLETED,
            label: "Completed",
            ...STATUS_COLORS[TASK_STATUS.COMPLETED],
          },
          {
            value: TASK_STATUS.ARCHIVED,
            label: "Archived",
            ...STATUS_COLORS[TASK_STATUS.ARCHIVED],
          },
          {
            value: TASK_STATUS.HELP_REQUIRED,
            label: "Help Required",
            ...STATUS_COLORS[TASK_STATUS.HELP_REQUIRED],
          },
        ];

        const handleOptionHover = (
          optionValue: string,
          element: HTMLButtonElement
        ) => {
          if (submenuTimeoutRef.current) {
            clearTimeout(submenuTimeoutRef.current);
          }

          if (optionValue === TASK_STATUS.HELP_REQUIRED) {
            const rect = element.getBoundingClientRect();
            const parentDropdown = element.closest('[role="listbox"]');
            const parentRect = parentDropdown?.getBoundingClientRect();

            if (parentRect) {
              setSubmenuPosition({
                top: rect.top + window.scrollY,
                left: parentRect.right + window.scrollX + 2,
                width: parentRect.width,
              });
            }
          } else {
            submenuTimeoutRef.current = setTimeout(() => {
              setSubmenuPosition(null);
            }, 100);
          }
        };

        const handleStatusChange = (newStatus: string) => {
          handleLocalChange(newStatus);

          if (newStatus !== TASK_STATUS.HELP_REQUIRED) {
            setHelpUserValue("");
            setSubmenuPosition(null);

            if (isNewTask && task.tempId && onNewTaskChange) {
              onNewTaskChange(task.tempId, "helpRequiredUser", "");
            } else if (
              !isNewTask &&
              hasRealId &&
              onExistingTaskChange &&
              task._id
            ) {
              onExistingTaskChange(task._id, "helpRequiredUser", "");
            }
          }
        };

        const handleUserSelect = (userId: string) => {
          setHelpUserValue(userId);
          setSubmenuPosition(null);

          if (isNewTask && task.tempId && onNewTaskChange) {
            onNewTaskChange(task.tempId, "helpRequiredUser", userId);
          } else if (
            !isNewTask &&
            hasRealId &&
            onExistingTaskChange &&
            task._id
          ) {
            onExistingTaskChange(task._id, "helpRequiredUser", userId);
          }
        };

        return (
          <>
            <CustomDropdown
              dropdownWidth={100}
              minDropdownWidth={160}
              value={String(editingValue || TASK_STATUS.TODO)}
              options={statusOptions}
              onChange={handleStatusChange}
              placeholder="Select Status"
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              autoFocus={true}
              autoOpen={true}
              onOptionHover={handleOptionHover}
              showArrowOnOption={TASK_STATUS.HELP_REQUIRED}
            />

            {submenuPosition &&
              typeof document !== "undefined" &&
              ReactDOM.createPortal(
                <div
                  ref={submenuRef}
                  className="fixed bg-white border border-gray-300 rounded-md shadow-xl overflow-hidden flex flex-col"
                  style={{
                    top: `${submenuPosition.top}px`,
                    left: `${submenuPosition.left}px`,
                    width: `${submenuPosition.width}px`,
                    maxHeight: "280px",
                    zIndex: 10000,
                  }}
                  onMouseEnter={() => {
                    if (submenuTimeoutRef.current) {
                      clearTimeout(submenuTimeoutRef.current);
                    }
                  }}
                  onMouseLeave={() => {
                    submenuTimeoutRef.current = setTimeout(() => {
                      setSubmenuPosition(null);
                    }, 200);
                  }}
                >
                  <div className="p-2 border-b border-gray-200">
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={submenuSearchTerm}
                      onChange={(e) => setSubmenuSearchTerm(e.target.value)}
                      className="w-full py-1 px-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>

                  <div className="overflow-auto flex-1">
                    {filteredUserOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 text-center">
                        No users found
                      </div>
                    ) : (
                      filteredUserOptions.map((user) => (
                        <button
                          key={user.value}
                          type="button"
                          className={clsx(
                            "w-full px-3 py-2 text-left text-sm transition-colors",
                            "hover:bg-blue-50 focus:bg-blue-50",
                            "border-b border-gray-100 last:border-0",
                            user.value === helpUserValue &&
                              "bg-blue-100 font-semibold text-blue-800"
                          )}
                          onClick={() => handleUserSelect(user.value)}
                        >
                          {user.label}
                        </button>
                      ))
                    )}
                  </div>
                </div>,
                document.body
              )}
          </>
        );

        
        case "frequency":
  const handleDateRangeChange = (type: 'startDate' | 'endDate', dateValue: string) => {
    const newDateRange = {
      ...dateRange,
      [type]: dateValue
    };
    setDateRange(newDateRange);

    // Save date range to task
    if (isNewTask && task.tempId && onNewTaskChange) {
      onNewTaskChange(task.tempId, type, dateValue);
    } else if (!isNewTask && hasRealId && onExistingTaskChange && task._id) {
      onExistingTaskChange(task._id, type, dateValue);
    }
  };

  return (
    <FrequencyPicker
      value={editingValue || FREQUENCY_OPTIONS.NONE}
      onChange={(value) => handleLocalChange(value)}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      autoFocus={true}
    />
  );



      case "dueDate":
        return (
          <ModernDatePicker
            value={editingValue || ""}
            onChange={(value: any) => handleLocalChange(value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus={true}
            placeholder="Select date"
            showColorLegend={false}
          />
        );

      case "task":
        const taskOptions = [
          { value: "task-1", label: "Task 1" },
          { value: "task-2", label: "Task 2" },
          { value: "task-3", label: "Task 3" },
          { value: "task-4", label: "Task 4" },
          { value: "task-5", label: "Task 5" },
        ];

        return (
          <CustomDropdown
            dropdownWidth={220}
            minDropdownWidth={220}
            value={editingValue || ""}
            options={taskOptions}
            onChange={(value) => {
              handleLocalChange(value);
              if (isNewTask && task.tempId && onNewTaskChange) {
                onNewTaskChange(task.tempId, "subTask", "");
              } else if (
                !isNewTask &&
                hasRealId &&
                onExistingTaskChange &&
                task._id
              ) {
                onExistingTaskChange(task._id, "subTask", "");
              }
            }}
            placeholder="Select Task"
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus={true}
            autoOpen={true}
            showAddButton={true}
            addLabel="Add"
            onAddClick={() => {
              const addHandler = dropdownConfig.task?.onAdd;
              if (addHandler) {
                addHandler();
              } else {
                console.log("Add Task clicked");
              }
            }}
          />
        );

      case "subTask":
        const getSubTaskOptions = (selectedTask: string) => {
          const subTaskMap: Record<
            string,
            Array<{ value: string; label: string }>
          > = {
            "task-1": [
              { value: "subtask-1-1", label: "Sub-Task 1.1" },
              { value: "subtask-1-2", label: "Sub-Task 1.2" },
              { value: "subtask-1-3", label: "Sub-Task 1.3" },
              { value: "subtask-1-4", label: "Sub-Task 1.4" },
              { value: "subtask-1-5", label: "Sub-Task 1.5" },
            ],
            "task-2": [
              { value: "subtask-2-1", label: "Sub-Task 2.1" },
              { value: "subtask-2-2", label: "Sub-Task 2.2" },
              { value: "subtask-2-3", label: "Sub-Task 2.3" },
            ],
            "task-3": [
              { value: "subtask-3-1", label: "Sub-Task 3.1" },
              { value: "subtask-3-2", label: "Sub-Task 3.2" },
            ],
            "task-4": [
              { value: "subtask-4-1", label: "Sub-Task 4.1" },
              { value: "subtask-4-2", label: "Sub-Task 4.2" },
              { value: "subtask-4-3", label: "Sub-Task 4.3" },
              { value: "subtask-4-4", label: "Sub-Task 4.4" },
            ],
            "task-5": [{ value: "subtask-5-1", label: "Sub-Task 5.1" }],
          };

          return subTaskMap[selectedTask] || [];
        };

        const selectedTask = task.task;
        const subTaskOptions = selectedTask
          ? getSubTaskOptions(selectedTask)
          : [];

        return (
          <CustomDropdown
            dropdownWidth={220}
            minDropdownWidth={220}
            value={editingValue || ""}
            options={subTaskOptions}
            onChange={(value) => handleLocalChange(value)}
            placeholder={selectedTask ? "Select Sub-Task" : "Select Task First"}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            autoFocus={true}
            autoOpen={true}
            showAddButton={!!selectedTask}
            addLabel="Add"
            onAddClick={() => {
              const addHandler = dropdownConfig.subTask?.onAdd;
              if (addHandler) {
                addHandler(selectedTask);
              } else {
                console.log(`Add Sub-Task clicked for task: ${selectedTask}`);
              }
            }}
            loading={false}
          />
        );

      case "et":
      case "tt":
        return (
          <input
            {...commonInputProps}
            type="number"
            min={0}
            onChange={(e) => handleLocalChange(e.target.value)}
            placeholder="0"
          />
        );

      default:
        return (
          <input
            {...commonInputProps}
            type="text"
            onChange={(e) => handleLocalChange(e.target.value)}
            placeholder={`Enter ${column.label}`}
          />
        );
    }
  };

  // Main render
  switch (colKey) {
    case "serial":
      const serialNum = isNewTask
        ? " "
        : serialStart > 0
        ? serialStart
        : index + 1;
      return (
        <Tooltip content={column.label}>
          <div
            className="w-full h-full flex items-center justify-center text-sm"
            style={{ height: "24px" }}
          >
            <span
              className={clsx(isNewTask && "text-orange-600 font-semibold")}
            >
              {serialNum}
            </span>
          </div>
        </Tooltip>
      );

    case "actions":
      if (isNewTask && !hasRealId) {
        return (
          <Tooltip content={column.label}>
            <div
              className="w-full h-full flex items-center justify-center text-sm"
              style={{ height: "24px" }}
            >
              <span className="text-gray-400">-</span>
            </div>
          </Tooltip>
        );
      }

      return (
        <Tooltip content={column.label}>
          <div
            className="w-full h-full flex items-center justify-center space-x-1"
            style={{ height: "24px" }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                openModal("quotation", task);
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Quotation"
            >
             <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.19995 5.60002H5.59995C6.04155 5.60002 6.39995 5.24162 6.39995 4.80002V2.40002" fill="#737373"/>
<path d="M3.19995 5.60002H5.59995C6.04155 5.60002 6.39995 5.24162 6.39995 4.80002V2.40002" stroke="#737373" stroke-width="1.5" stroke-linejoin="round"/>
<path d="M10 7.20001H8" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M10 10.4H6" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M12.8 11.2V4.80002C12.8 3.47442 11.7256 2.40002 10.4 2.40002H6.73115C6.51915 2.40002 6.31515 2.48402 6.16555 2.63442L3.43435 5.36562C3.28395 5.51602 3.19995 5.71922 3.19995 5.93122V11.2C3.19995 12.5256 4.27435 13.6 5.59995 13.6H10.4C11.7256 13.6 12.8 12.5256 12.8 11.2Z" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openModal("comment", task);
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Comment"
            >
           <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.9999 2.40002C4.9071 2.40002 2.3999 4.90722 2.3999 8.00002C2.3999 9.10082 2.7223 10.124 3.2703 10.9896L2.3999 13.6L5.0103 12.7296C5.8759 13.2776 6.8991 13.6 7.9999 13.6C11.0927 13.6 13.5999 11.0928 13.5999 8.00002C13.5999 4.90722 11.0927 2.40002 7.9999 2.40002Z" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openModal("time", task);
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Time"
            >
             <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.6439 2.40002H4.7999C3.4743 2.40002 2.3999 3.47442 2.3999 4.80002V11.2C2.3999 12.5256 3.4743 13.6 4.7999 13.6H11.1999C12.5255 13.6 13.5999 12.5256 13.5999 11.2V8.36402" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M6.3999 9.60005L6.7999 7.20005L11.3655 2.64085C11.6783 2.32805 12.1847 2.32805 12.4967 2.64085L13.3655 3.50965C13.6783 3.82245 13.6783 4.32885 13.3655 4.64085L8.7999 9.20005L6.3999 9.60005Z" stroke="#737373" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openModal("delete", task);
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Delete"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M13.5999 4H2.3999" stroke="#F43F5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M9.5999 2.40002H6.3999V4.00002H9.5999V2.40002Z" fill="#F43F5E" stroke="#F43F5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11.9599 6.40002L11.6759 12.08C11.6335 12.9312 10.9303 13.6 10.0775 13.6H5.92146C5.06866 13.6 4.36626 12.9312 4.32306 12.08L4.03906 6.40002" stroke="#F43F5E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

            </button>
          </div>
        </Tooltip>
      );

    default:
      const value = task[colKey as keyof Task];
      const displayValue =
        colKey === "status"
          ? getStatusDisplay(task, users)
          : getDisplayValue(value, colKey);
      const hasValue = value !== null && value !== undefined && value !== "";

      return (
        <Tooltip content={column.label}>
          <div
            ref={cellRef}
            className={clsx(
              "cursor-pointer",
              "w-full h-full -px-10",
              !hasValue && !isEditing && "text-gray-400 italic",
              isNewTask && "bg-yellow-50/30"
            )}
            style={{ height: "24px" }}
            onClick={handleCellClick}
            data-task-id={task.tempId || task._id || task.id}
            data-column-key={colKey}
            data-excel-cell="true"
          >
            {isEditing ? (
              renderEditInterface()
            ) : (
              <div
                className="w-full h-full flex items-center px-2 text-sm"
                style={{ height: "24px" }}
              >
                {colKey === "priority" && hasValue ? (
                  <div className="w-full">
                    <PriorityBadge priority={value as string} />
                  </div>
                ) : colKey === "status" && hasValue ? (
                  <span                     className={clsx(
                      "inline-flex justify-center items-center w-full px-2 py-0 rounded text-sm font-medium",
                      COLORS.status[task.status as string] ||
                        "bg-gray-100 text-gray-700"
                    )}
                    style={{ height: "20px" }}
                  >
                    {displayValue}
                  </span>
                ) : colKey === "dueDate" && hasValue ? (
                  <span className="text-red-600 font-medium text-sm">
                    {new Date(value).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                ) : (
                  <span className="truncate w-full text-sm">
                    {displayValue}
                  </span>
                )}
              </div>
            )}
          </div>
        </Tooltip>
      );
  }
};

export default BaseTableCell;